'use client'

import { useEffect, useState } from 'react'
import Swiper from 'swiper'
import { Pagination } from 'swiper/modules'
import { useIsomorphicLayoutEffect } from '../../../core/hook/useIsomorphicLayout'
import { useIsMobileOrTablet } from '../../utils/screen.utils'
import { Button } from '../Button/Button'
import { Tag } from '../Tag/tag'
import { AssetType, ProductAssetImageType } from './ProductAssets.types'
import ProductAssetsFullScreen from './ProductAssetsFullScreen'
import { useTranslation } from '../../context/ConfigContext'
import Image from '../Image/Image'
import { handleError } from '../../../core/hook/utils'
import { ProductVideo } from './ProductVideo'

const ProductAssetImage = ({ image }: { image: ProductAssetImageType }) => (
  <div className='product-asset-image'>
    <picture>
      <Image src={image.messika_product_large} alt={image.alt} priority={true} size='l' />
      <Tag
        isExcluWeb={image.isExcluWeb ?? 0}
        isNew={image.isNew ?? 0}
        isIconique={image.isIconique ?? 0}
      />
    </picture>
  </div>
)

type ProductAssetsProps = {
  assets: Array<AssetType>
  className?: string
  labelButton?: string
  defaultNumberOfShowedAssets?: number
  seeAllImagesByDefault?: boolean
}

const ProductAssets = ({
  assets,
  className,
  seeAllImagesByDefault,
  labelButton,
  defaultNumberOfShowedAssets = 5,
}: ProductAssetsProps) => {
  const isTablet = useIsMobileOrTablet()

  let gallery = assets

  if (!isTablet) {
    if (assets.length >= defaultNumberOfShowedAssets) {
      seeAllImagesByDefault = false
      gallery = assets.slice(0, 3)
    } else {
      seeAllImagesByDefault = true
    }
  } else {
    gallery = assets
  }

  const [seeAllAssets, setSeeAllAssets] = useState(seeAllImagesByDefault)
  const [assetToPreview, setAssetToPreview] = useState<AssetType>()
  const t = useTranslation()

  if (seeAllAssets === true) {
    gallery = assets
  }

  const openFullScreenModal = (asset: AssetType) => {
    setAssetToPreview(asset)
    document.body.classList.add('fullscreen')
    document.body.classList.add('transition-fullscreen')
  }
 
  useEffect(() => {
    if (assetToPreview) {
      return () => {
        document.body.classList.remove('transition-fullscreen')
        document.body.classList.remove('fullscreen')
      }
    }
  }, [assetToPreview]);

  useIsomorphicLayoutEffect(() => {
    if (!isTablet) {
      return () => {}
    }

    const swiper = new Swiper('.product-assets .product-assets-swiper', {
      watchSlidesProgress: true,
      modules: [Pagination],
      spaceBetween: 8,
      slidesPerView: 1,
      pagination: {
        clickable: false,
        el: '.product-assets .custom-slider-pagination',
        bulletClass: 'custom-slider-pagination-bullet',
        bulletActiveClass: 'custom-slider-pagination-bullet-active',
      },
      breakpoints: {
        960: {
          enabled: false,
        },
      },
    })

    return () => {
      try {
        swiper.destroy?.()
      } catch (error) {
        // TODO workaround
        handleError(error, false)
      }
    }
  }, [isTablet])


  return (
    <div className={`product-assets ${className ?? ''}`}>
      <div className='product-assets-swiper'>
        <ul className='product-assets-list swiper-wrapper'>
          {gallery.map((asset) => (
            <li
              className={`swiper-slide product-assets-item`}
              onClick={() => openFullScreenModal(asset)}
              aria-hidden='true'
              key={asset.id}>
              {asset.type === 'image' ? (
                <ProductAssetImage image={asset} {...asset} />
              ) : (
                <ProductVideo video={asset} classname='product-asset-video' />
              )}
            </li>
          ))}
        </ul>
        {isTablet && <div className='custom-slider-pagination' />}
      </div>
      {assets.length > defaultNumberOfShowedAssets && !seeAllAssets && (
        <div className='product-assets-seeall'>
          <Button
            onClick={() => setSeeAllAssets(true)}
            label={labelButton ?? t('catalog-product-image-see-more', {}, false)}
            style='very-light'
            classname='simple-underline'
          />
        </div>
      )}
      {assetToPreview && (
        <ProductAssetsFullScreen
          assets={assets}
          isTablet={isTablet}
          onClose={() => {
            setAssetToPreview(undefined)
            document.body.classList.remove('transition-fullscreen')
            document.body.classList.remove('fullscreen')
          }}
          assetToPreview={assetToPreview}
        />
      )}
    </div>
  )
}

export default ProductAssets
